import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Home, ArrowUpRight } from "lucide-react";

import { api } from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import { BreadcrumbJsonLd, OrganizationJsonLd } from "@/components/Schemas";

export default function HtmlSitemap() {
  const [data, setData] = useState(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "https://checkexpert.be";

  useEffect(() => {
    api.get("/seo-pages/checks").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const breadcrumbs = [{ name: "Sitemap", url: "/sitemap" }];

  return (
    <div className="bg-background font-modern min-h-screen" data-testid="html-sitemap">
      <Helmet>
        <title>Sitemap — alle pagina's van CheckExpert</title>
        <meta name="description" content="Overzichtelijke sitemap van alle pagina's op CheckExpert: alle 25 documentchecks, intent-pagina's, info en juridisch." />
        <link rel="canonical" href={`${origin}/sitemap`} />
      </Helmet>
      <OrganizationJsonLd origin={origin} />
      <BreadcrumbJsonLd items={breadcrumbs} origin={origin} />

      <div className="max-w-6xl mx-auto px-6 pt-10 pb-20">
        <Breadcrumbs items={breadcrumbs} />

        <header className="mt-6 max-w-3xl">
          <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#002FA7]">Sitemap</span>
          <h1 className="font-editorial text-5xl sm:text-6xl font-medium text-ink mt-3 leading-[1.04]" style={{ letterSpacing: "-0.025em" }}>
            Alle pagina's in één overzicht
          </h1>
          <p className="text-ink-soft text-lg mt-5 leading-relaxed">
            Vind hier elke pagina van CheckExpert, mooi gegroepeerd per onderwerp. Voor zoekmachines is er ook een <a href="/api/seo/sitemap.xml" className="text-[#002FA7] underline underline-offset-4">XML-versie</a>.
          </p>
        </header>

        <div className="mt-12 grid lg:grid-cols-[1fr_2fr] gap-10">
          {/* Sidebar: kernpagina's */}
          <aside>
            <section className="rounded-2xl bg-white border border-border p-6">
              <h2 className="font-editorial text-xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
                Kernpagina's
              </h2>
              <ul className="mt-4 space-y-2.5 text-sm">
                <SmLink to="/" label="Home" icon={<Home className="h-3.5 w-3.5" />} />
                <SmLink to="/checks" label="Alle checks (hub)" />
                <SmLink to="/hoe-het-werkt" label="Hoe het werkt" />
                <SmLink to="/prijzen" label="Prijzen" />
                <SmLink to="/over-ons" label="Over ons" />
                <SmLink to="/contact" label="Contact" />
                <SmLink to="/faq" label="FAQ" />
                <SmLink to="/blog" label="Blog" />
              </ul>
            </section>

            <section className="rounded-2xl bg-white border border-border p-6 mt-6">
              <h2 className="font-editorial text-xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
                Voor bedrijven
              </h2>
              <ul className="mt-4 space-y-2.5 text-sm">
                <SmLink to="/voor-bedrijven" label="B2B portaal" />
                <SmLink to="/voor-bedrijven/aanmelden" label="Bedrijf aanmelden" />
              </ul>
            </section>

            <section className="rounded-2xl bg-white border border-border p-6 mt-6">
              <h2 className="font-editorial text-xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
                Juridisch
              </h2>
              <ul className="mt-4 space-y-2.5 text-sm">
                <SmLink to="/privacy" label="Privacybeleid" />
                <SmLink to="/algemene-voorwaarden" label="Algemene voorwaarden" />
                <SmLink to="/cookiebeleid" label="Cookiebeleid" />
              </ul>
            </section>
          </aside>

          {/* Hoofd: checks gegroepeerd per categorie */}
          <div>
            <h2 className="font-editorial text-2xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
              19 documentchecks
            </h2>
            <p className="text-sm text-ink-soft mt-2">Elke check heeft twee pagina's: een merkpagina <code className="text-[#002FA7]">/checks/...</code> en een intent-pagina <code className="text-[#002FA7]">/...-controleren</code>.</p>

            {data?.categories?.map((cat) => (
              <section key={cat.slug} className="mt-7" data-testid={`sm-cat-${cat.slug}`}>
                <div className="flex items-baseline gap-2.5 mb-3">
                  <span className="h-2 w-2 rounded-full" style={{ background: cat.color }} aria-hidden />
                  <h3 className="font-editorial text-lg font-semibold text-ink">{cat.label}</h3>
                  <span className="text-xs text-ink-subtle">{cat.checks.length} {cat.checks.length === 1 ? "check" : "checks"}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {cat.checks.map((c) => (
                    <div key={c.slug} className="rounded-xl border border-border bg-white p-4">
                      <h4 className="font-editorial text-base font-semibold text-ink leading-snug">{c.display_name || c.name}</h4>
                      <ul className="mt-2 space-y-1 text-[13px]">
                        <li>
                          <Link to={`/checks/${c.slug}`} className="inline-flex items-center gap-1 text-[#002FA7] hover:underline underline-offset-2" data-testid={`sm-brand-${c.slug}`}>
                            <ArrowUpRight className="h-3 w-3" /> /checks/{c.slug}
                          </Link>
                          <span className="text-ink-subtle ml-1.5">— merk­pagina</span>
                        </li>
                        {c.theme_slug && (
                          <li>
                            <Link to={`/${c.theme_slug}`} className="inline-flex items-center gap-1 text-[#002FA7] hover:underline underline-offset-2" data-testid={`sm-intent-${c.slug}`}>
                              <ArrowUpRight className="h-3 w-3" /> /{c.theme_slug}
                            </Link>
                            <span className="text-ink-subtle ml-1.5">— intent</span>
                          </li>
                        )}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>
            ))}

            <section className="mt-10 pt-7 border-t border-border">
              <h2 className="font-editorial text-xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
                Voor zoekmachines &amp; AI
              </h2>
              <ul className="mt-3 space-y-1.5 text-sm">
                <li><a href="/api/seo/sitemap.xml" className="text-[#002FA7] hover:underline underline-offset-4">sitemap.xml (index)</a></li>
                <li><a href="/api/seo/robots.txt" className="text-[#002FA7] hover:underline underline-offset-4">robots.txt</a></li>
                <li><a href="/api/seo/llms.txt" className="text-[#002FA7] hover:underline underline-offset-4">llms.txt — gecureerde AI-index</a></li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

function SmLink({ to, label, icon }) {
  return (
    <li>
      <Link to={to} className="inline-flex items-center gap-1.5 text-ink hover:text-[#002FA7] transition-colors">
        {icon}
        <span>{label}</span>
      </Link>
    </li>
  );
}
